import { createPortal } from "react-dom";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const VIEWPORT_MARGIN_PX = 8;
const DEFAULT_MAX_WIDTH = 240;
const DEFAULT_OFFSET = 8;
const DEFAULT_Z_INDEX = 2147483647;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export default function InfoTooltip({
  text,
  label,
  ariaLabel: ariaLabelProp,
  placement = "top",
  maxWidth = DEFAULT_MAX_WIDTH,
  offset = DEFAULT_OFFSET,
  size = "compact",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({
    top: -9999,
    left: -9999,
    ready: false,
  });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const closeTimerRef = useRef(null);

  const canShow = Boolean(text);
  const ariaLabel = ariaLabelProp || (label ? `Help: ${label}` : "Help");

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openTooltip = useCallback(() => {
    if (!canShow) return;
    clearCloseTimer();
    setOpen(true);
  }, [canShow, clearCloseTimer]);

  const closeTooltip = useCallback(() => {
    clearCloseTimer();
    setOpen(false);
    setPosition((prev) => ({ ...prev, ready: false }));
  }, [clearCloseTimer]);

  const scheduleClose = useCallback(
    (delay = 80) => {
      clearCloseTimer();
      closeTimerRef.current = setTimeout(() => {
        closeTooltip();
      }, delay);
    },
    [clearCloseTimer, closeTooltip]
  );

  const updatePosition = useCallback(() => {
    if (!open || !triggerRef.current || !tooltipRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    const spaceAbove = triggerRect.top - VIEWPORT_MARGIN_PX;
    const spaceBelow =
      window.innerHeight - triggerRect.bottom - VIEWPORT_MARGIN_PX;
    const preferred = placement === "bottom" ? "bottom" : "top";
    let nextPlacement = preferred;

    if (
      preferred === "top" &&
      spaceAbove < tooltipRect.height + offset &&
      spaceBelow > spaceAbove
    ) {
      nextPlacement = "bottom";
    } else if (
      preferred === "bottom" &&
      spaceBelow < tooltipRect.height + offset &&
      spaceAbove > spaceBelow
    ) {
      nextPlacement = "top";
    }

    const topCandidate =
      nextPlacement === "top"
        ? triggerRect.top - tooltipRect.height - offset
        : triggerRect.bottom + offset;
    const top = clamp(
      topCandidate,
      VIEWPORT_MARGIN_PX,
      window.innerHeight - tooltipRect.height - VIEWPORT_MARGIN_PX
    );

    const leftCandidate =
      triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
    const maxLeft =
      window.innerWidth - tooltipRect.width - VIEWPORT_MARGIN_PX;
    const left = clamp(leftCandidate, VIEWPORT_MARGIN_PX, Math.max(VIEWPORT_MARGIN_PX, maxLeft));

    setPosition({ top, left, ready: true });
  }, [open, placement, offset]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    let raf1 = null;
    let raf2 = null;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        updatePosition();
      });
    });
    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [open, updatePosition, text, label]);

  useEffect(() => {
    if (!open) return;
    const handleResize = () => updatePosition();
    const handleScroll = () => updatePosition();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (event) => {
      const trigger = triggerRef.current;
      const tooltip = tooltipRef.current;
      if (trigger?.contains(event.target) || tooltip?.contains(event.target)) {
        return;
      }
      closeTooltip();
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeTooltip();
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, closeTooltip]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  const handleToggleClick = useCallback(
    (event) => {
      if (!canShow) return;
      event.preventDefault();
      event.stopPropagation();
      setOpen((prev) => !prev);
    },
    [canShow]
  );

  const tooltipContent = (
    <div
      ref={tooltipRef}
      role="tooltip"
      onMouseEnter={openTooltip}
      onMouseLeave={() => scheduleClose()}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
      className={`fixed border border-slate-200 bg-white font-medium text-slate-700 shadow-lg ${
        size === "compact" ? "rounded-lg" : "rounded-xl"
      }`}
      style={{
        top: position.top,
        left: position.left,
        maxWidth,
        zIndex: DEFAULT_Z_INDEX,
        padding: size === "compact" ? "8px 10px" : "8px 12px",
        fontSize: size === "compact" ? "12px" : "13px",
        lineHeight: size === "compact" ? "1.25" : "1.35",
        whiteSpace: "pre-line",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        pointerEvents: "auto",
        opacity: 1,
        transform: position.ready ? "translateY(0)" : "translateY(2px)",
        transition: "opacity 120ms ease, transform 120ms ease",
        isolation: "isolate",
        backgroundColor: "white",
      }}
    >
      {label && (
        <div className="text-xs font-semibold text-slate-900 mb-1">
          {label}
        </div>
      )}
      <div className="text-slate-700">{text}</div>
    </div>
  );

  if (!canShow) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        onMouseEnter={openTooltip}
        onMouseLeave={() => scheduleClose()}
        onFocus={openTooltip}
        onBlur={() => scheduleClose()}
        onClick={handleToggleClick}
        className={`inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-[11px] font-semibold text-slate-400 shadow-sm transition hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1 ${className}`}
      >
        i
      </button>
      {open && typeof document !== "undefined"
        ? createPortal(tooltipContent, document.body)
        : null}
    </>
  );
}
